import { useEffect, useState } from "react";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { postBrochureRequest, saveLead } from "@/context/BrochureGateContext";

const EMPTY = { email: "", organisatie: "", naam: "", functie: "", telefoon: "", nieuwsbrief: false, website: "" };

const fields = [
  { k: "email", label: "E-mailadres", type: "email", required: true, auto: "email" },
  { k: "organisatie", label: "Gemeente of organisatie", required: true, auto: "organization" },
  { k: "naam", label: "Naam", auto: "name" },
  { k: "functie", label: "Functie", auto: "organization-title" },
  { k: "telefoon", label: "Telefoon", type: "tel", auto: "tel" },
];

function Field({ f, value, onChange }) {
  return (
    <label className={f.required ? "sm:col-span-2 block" : "block"}>
      <span className="text-[11px] uppercase tracking-[0.2em] text-ll-navy/60">
        {f.label} {f.required ? <span className="text-ll-coral">*</span> : <span className="normal-case tracking-normal">(optioneel)</span>}
      </span>
      <input
        type={f.type || "text"}
        required={f.required}
        autoComplete={f.auto}
        value={value}
        onChange={(e) => onChange(f.k, e.target.value)}
        data-testid={`brochure-gate-${f.k}`}
        className="mt-2 w-full bg-white border border-ll-navy/15 focus:border-ll-coral focus:outline-none focus:ring-2 focus:ring-ll-coral/20 rounded-xl px-4 py-3 text-ll-navy transition-colors"
      />
    </label>
  );
}

function Success({ doc }) {
  return (
    <div data-testid="brochure-gate-success" className="py-4">
      <div className="w-12 h-12 rounded-full bg-ll-coral text-white flex items-center justify-center">
        <Check size={22} />
      </div>
      <DialogTitle className="mt-6 font-heading text-3xl font-light text-ll-navy">Dank je. Hij staat klaar.</DialogTitle>
      <DialogDescription className="mt-3 text-ll-navy/70 leading-relaxed">
        We hebben de link ook naar je gemaild, voor later.
      </DialogDescription>
      <a
        href={doc.href}
        target="_blank"
        rel="noreferrer"
        data-gate-skip
        data-testid="brochure-gate-open-link"
        className="mt-8 inline-flex items-center gap-3 bg-ll-coral hover:bg-ll-navy text-white rounded-full pl-6 pr-2 py-2 transition-colors"
      >
        Open de {doc.title.toLowerCase().includes("one-pager") ? "one-pager" : "brochure"}
        <span className="w-9 h-9 rounded-full bg-white text-ll-navy flex items-center justify-center"><ArrowUpRight size={16} /></span>
      </a>
    </div>
  );
}

export default function BrochureGateDialog({ doc, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (doc) { setState("idle"); setError(""); }
  }, [doc]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    setError("");
    try {
      const res = await postBrochureRequest({ ...form, doc: doc.key });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === "string" ? data.detail : "Controleer je gegevens en probeer het opnieuw.");
      }
      const { website, nieuwsbrief, ...lead } = form;
      saveLead(lead);
      setState("done");
    } catch (err) {
      setError(err.message || "Er ging iets mis. Probeer het later opnieuw.");
      setState("idle");
    }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="brochure-gate-dialog" className="bg-[#FAF7F1] border-ll-navy/10 rounded-3xl sm:rounded-3xl max-w-lg max-h-[92vh] overflow-y-auto p-7 sm:p-10">
        {doc && state === "done" && <Success doc={doc} />}
        {doc && state !== "done" && (
          <form onSubmit={submit}>
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">{doc.title}</p>
            <DialogTitle className="mt-4 font-heading text-3xl sm:text-4xl font-light leading-tight text-ll-navy">
              Waar mogen we hem <span className="italic text-ll-coral">naartoe sturen?</span>
            </DialogTitle>
            <DialogDescription className="mt-3 text-sm text-ll-navy/70 leading-relaxed">
              Vul je gegevens in en je kunt hem meteen openen. We sturen de link ook per mail.
            </DialogDescription>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {fields.map((f) => <Field key={f.k} f={f} value={form[f.k]} onChange={set} />)}
            </div>
            <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />
            <label className="mt-6 flex items-start gap-3 cursor-pointer">
              <Checkbox checked={form.nieuwsbrief} onCheckedChange={(v) => set("nieuwsbrief", !!v)} data-testid="brochure-gate-nieuwsbrief" className="mt-0.5 border-ll-navy/30 data-[state=checked]:bg-ll-coral data-[state=checked]:border-ll-coral" />
              <span className="text-sm text-ll-navy/80">Houd mij op de hoogte. Hooguit vier keer per jaar, over pilots en wat we leren.</span>
            </label>
            {error && <p data-testid="brochure-gate-error" className="mt-5 text-sm text-ll-coral">{error}</p>}
            <button
              type="submit"
              disabled={state === "busy"}
              data-testid="brochure-gate-submit"
              className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-ll-navy hover:bg-ll-coral disabled:opacity-60 text-white rounded-full px-6 py-4 transition-colors"
            >
              {state === "busy" ? <Loader2 size={18} className="animate-spin" /> : null}
              {state === "busy" ? "Even geduld" : "Download"}
            </button>
            <p className="mt-4 text-[11px] text-ll-navy/50 leading-relaxed">
              We gebruiken je gegevens alleen om contact te houden over LevelLeven. Nooit voor iets anders.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
